import { configureStore } from "@reduxjs/toolkit";
import * as R from "ramda";
import {
  Provider,
  type TypedUseSelectorHook,
  useDispatch as useAppDispatch,
  useSelector as useAppSelector,
} from "react-redux";
import { type Middleware, combineReducers } from "redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  type Persistor,
  REGISTER,
  REHYDRATE,
  type Storage,
  persistReducer,
  persistStore,
} from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import CustomizerReducer from "./customizer/CustomizerSlice";
import DealsReducer from "./deals/DealsSlice";
import FiltersReducer from "./filters/FiltersSlice";
import OffersReducer from "./offers/OffersSlice";
import PlacesReducer from "./places/PlacesSlice";
import UsersReducer from "./users/UsersSlice";
import WatchlistsReducer from "./watchlists/WatchlistsSlice";

const errorSerializerMiddleware: Middleware = () => (next) => (action) => {
  if (action.payload instanceof Error) {
    action.payload = R.mergeRight(
      R.pick(["code", "message", "name", "status"], action.payload),
      {
        response: action.payload.response
          ? R.pick(["data", "status", "statusText"], action.payload.response)
          : undefined,
      },
    );
  }
  return next(action);
};

export const createStore = (storage: Storage) => {
  const rootReducer = combineReducers({
    customizer: CustomizerReducer,
    offersReducer: OffersReducer,
    usersReducer: UsersReducer,
    filtersReducer: FiltersReducer,
    watchlistsReducer: WatchlistsReducer,
    dealsReducer: DealsReducer,
    places: PlacesReducer,
  });

  const persistedReducer = persistReducer(
    {
      key: "root",
      storage,
      whitelist: ["places"],
    },
    rootReducer,
  );
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(errorSerializerMiddleware),
  });
};

type StoreType = ReturnType<typeof createStore>;

let persistor: Persistor | null = null;
let store: ReturnType<typeof createStore> | null = null;

export const initStore = (newStore: ReturnType<typeof createStore>) => {
  store = newStore;
  persistor = persistStore(store);
};

const rootReducer = combineReducers({
  customizer: CustomizerReducer,
  offersReducer: OffersReducer,
  usersReducer: UsersReducer,
  filtersReducer: FiltersReducer,
  watchlistsReducer: WatchlistsReducer,
  dealsReducer: DealsReducer,
  places: PlacesReducer,
});

export type AppState = ReturnType<typeof rootReducer>;
export type AppDispatch = StoreType["dispatch"];
export const getDispatch = (store: StoreType): AppDispatch => store.dispatch;
export const useDispatch = () => useAppDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<AppState> = useAppSelector;

export function StoreWrapper({ children }: { children: React.ReactNode }) {
  if (!store || !persistor) {
    throw new Error("Store is not initialized. Please call initStore first.");
  }
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>{children}</PersistGate>
    </Provider>
  );
}
